import {useCallback, useEffect, useMemo, useState} from 'react';
import {GetFormattedRewardCatalog} from '@/models/RewardCatalogModel';
import {useDataTable} from '@/hooks/useDataTable-hook';
import {buildQueryParams} from '@/utils/commons';
import {
  getRewardCatalogCsvFile,
  getRewardCatalog,
  downloadTemplate,
  handleBulkUploadAPI,
  downloadDigitalCodesTemplate
} from '@/services/rewardCatalog';
import {invalidateDigitalCodesListCache} from '@/services/digitalCodes';
import {RewardCatalogListColConfig} from '@/services/MockData/RewardCatalogList/RewardCatalogListColConfig';
import {useQueries, useQueryClient} from '@tanstack/react-query';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {DROPDOWN_TYPES} from '@/utils/constants/dropdownConstants';
import {useDropdown} from './Dropdown-hook';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import {
  Constants,
  FILE_UPLOAD_TEMPLATE_TYPE
} from '@/utils/constants/commonConstants';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  setOpenUpload as setOpenUploadStore,
  setUploadProgress as setUploadProgressStore,
  setUploadErrorMessage as setUploadErrorMessageStore,
  setUploading as setUploadingStore,
  setSuccessfullyUploaded as setSuccessfullyUploadedStore,
  setFormData as setFormDataStore,
  selectOpenUpload
} from '@/store/slices/rewardBulkUpload';
import {
  customRules,
  createCustomRulesRewards
} from '@/services/MockData/BulkUpload/customRules';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {logger} from '@/helpers/logger';
import {createDownloadRewardReport} from '@/helpers/rewardsUtils';

const defaultSortColumn =
  RewardCatalogListColConfig?.[6]?.name_of_db_column ??
  RewardCatalogListColConfig?.[0]?.name_of_db_column;

const initialSortConfig = defaultSortColumn
  ? {
      column: defaultSortColumn,
      direction: 'desc'
    }
  : {};
export const useRewardCatalog = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const {downloadFromBlobUrl} = useAzureBlob();
  const countryCode = useAppSelector(selectCountryCode);
  const {
    setPageIndex,
    setSortingObj,
    setSearchArry,
    setSearchArrImmediate,
    pageIndex,
    sortingObj,
    searchArr,
    handleResetAll
  } = useDataTable(0, false, false, initialSortConfig);
  const {dropdownList} = useDropdown({
    dropdownType: DROPDOWN_TYPES.RewardsCatalogAction
  });

  const customRulesRewards = useMemo(() => {
    const languages = COUNTRY_LANGUAGES_CODES[countryCode] || [];
    return createCustomRulesRewards(languages);
  }, [countryCode]);

  const progressInterval = 200;
  const simulatedProgressMax = 90;
  const simulatedProgressIncrement = 5;
  const [openUpload, setOpenUpload] = useState(false);
  const [formData, setFormData] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [successfullyUploaded, setSuccessfullyUploaded] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');

  const setOpenUploadWithDispatch = (value) => {
    setOpenUpload(value);
    dispatch(setOpenUploadStore(value));
  };

  const setFormDataWithDispatch = (value) => {
    setFormData(value);
    dispatch(setFormDataStore(value));
  };

  const setUploadProgressWithDispatch = (value) => {
    setUploadProgress(value);
    dispatch(setUploadProgressStore(value));
  };

  const setUploadingWithDispatch = (value) => {
    setUploading(value);
    dispatch(setUploadingStore(value));
    if (value === true && uploadErrorMessage) {
      setUploadErrorMessageWithDispatch('');
    }
  };

  const setSuccessfullyUploadedWithDispatch = (value) => {
    setSuccessfullyUploaded(value);
    dispatch(setSuccessfullyUploadedStore(value));
  };

  const setUploadErrorMessageWithDispatch = (value) => {
    setUploadErrorMessage(value);
    dispatch(setUploadErrorMessageStore(value));
  };

  // Get Redux state to sync with local state
  const reduxOpenUpload = useAppSelector(selectOpenUpload);

  // Sync Redux state with local state when Redux state changes from other components
  useEffect(() => {
    if (reduxOpenUpload) {
      setOpenUpload(reduxOpenUpload);
    }
  }, [reduxOpenUpload]);

  // Helper to build query params, optionally including pagination
  const getQueryParams = useCallback(
    ({includePagination = true} = {}) => {
      return buildQueryParams({
        ...(sortingObj?.column && {
          sortBy: sortingObj.column,
          order: sortingObj.direction
        }),
        ...(includePagination && {
          pageSize: 10,
          page: pageIndex || 0
        })
      });
    },
    [sortingObj?.column, sortingObj?.direction, pageIndex]
  );

  const queryParams = useMemo(
    () => getQueryParams({includePagination: true}),
    [getQueryParams]
  );

  const [{data, isLoading, isFetching}] = useQueries({
    queries: [
      {
        queryKey: ['REWARD_CATALOG', queryParams, searchArr],
        queryFn: () => getRewardCatalog(queryParams, searchArr)
      }
    ]
  });

  const resultsData = useMemo(() => data || {}, [data]);
  const rewardCatalogData = useMemo(
    () => GetFormattedRewardCatalog(resultsData),
    [resultsData]
  );

  const onDownloadBtnClick = async (selectedIds, excludeIds = []) => {
    const requestBody = {
      ids: selectedIds,
      excludeIds,
      filters: searchArr
    };

    try {
      // Exclude pagination params for download
      const downloadQueryParams = getQueryParams({includePagination: false});
      const data = await getRewardCatalogCsvFile(
        downloadQueryParams,
        requestBody
      );
      const fileName = `RewardCatalog_${getDateWithTimeString()}`;
      downloadCSVFiles(data, fileName);
    } catch (error) {
      logger.error('Error downloading CSV file:', error);
    }
  };

  // Extract sortBy and order from API response and sync with sortingObj
  useEffect(() => {
    if (resultsData?.sortBy && resultsData?.order) {
      // Only update if different to avoid infinite loop
      if (
        resultsData.sortBy !== sortingObj?.column ||
        resultsData.order !== sortingObj?.direction
      ) {
        setSortingObj({
          column: resultsData.sortBy,
          direction: resultsData.order
        });
      }
    }
  }, [resultsData, sortingObj, setSortingObj]);

  const handleDownloadTemplate = async (uploadFor) => {
    try {
      if (uploadFor === Constants.BulkRewards) {
        const response = await downloadTemplate();
        if (!response) return;
        const fileName = `BulkUploadRewardTemplate_${getDateWithTimeString()}`;
        downloadCSVFiles(response, fileName);
      }
      if (uploadFor === Constants.BulkDigitalCodes) {
        const response = await downloadDigitalCodesTemplate();
        if (!response) return;
        const fileName = `DigitalCodesTemplate_${getDateWithTimeString()}`;
        downloadCSVFiles(response, fileName);
      }
    } catch (error) {
      logger.error('Error downloading CSV file:', error);
    }
  };

  const handleCloseNotification = () => {
    setOpenUploadWithDispatch(false);
  };

  const handleBulkUpload = async (file, uploadFor) => {
    const requestBody = {
      fileUrl: file?.storeList,
      templateType:
        uploadFor === Constants.BulkDigitalCodes
          ? FILE_UPLOAD_TEMPLATE_TYPE.DIGITAL_CODES
          : FILE_UPLOAD_TEMPLATE_TYPE.REWARD_CATALOG
    };

    setUploadingWithDispatch(true);
    const stopProgress = simulateProgress();

    try {
      const response = await handleBulkUploadAPI(queryParams, requestBody);

      setUploadProgressWithDispatch(100);
      if (response?.success) {
        if (
          response?.data?.validationErrors &&
          response?.data?.validationErrors?.length > 0
        ) {
          setUploadErrorMessageWithDispatch(response);
          setFormDataWithDispatch(null);
          return false;
        } else {
          if (uploadFor === Constants.BulkDigitalCodes) {
            invalidateDigitalCodesListCache();
          }
          if (uploadFor === Constants.BulkRewards) {
            queryClient.invalidateQueries({queryKey: ['REWARD_CATALOG']});
          }
          setOpenUploadWithDispatch(false);
          setFormDataWithDispatch(null);
          setUploadProgressWithDispatch(0);
          setUploadingWithDispatch(false);
          setUploadErrorMessageWithDispatch('');
          setSuccessfullyUploadedWithDispatch(true);
          setSortingObj({column: 'updatedDate', direction: 'desc'});
          setTimeout(() => {
            setSuccessfullyUploadedWithDispatch(false);
          }, 30000);
          return true;
        }
      } else {
        const errorMsg = response?.message;
        setUploadErrorMessageWithDispatch(errorMsg);
        return false;
      }
    } catch (error) {
      setFormDataWithDispatch((prev) => {
        return {...prev, targetMediaFile: [], storeList: null};
      });
      setUploadErrorMessageWithDispatch(error?.cause || error?.message);
      return false;
    } finally {
      setUploadingWithDispatch(false);
      stopProgress();
    }
  };

  const simulateProgress = () => {
    setUploadProgressWithDispatch(0);
    const interval = setInterval(() => {
      setUploadProgress((currentProgress) => {
        const newProgress = currentProgress + simulatedProgressIncrement;
        if (newProgress >= simulatedProgressMax) {
          clearInterval(interval);
          return simulatedProgressMax;
        }
        return newProgress;
      });
    }, progressInterval);

    return () => clearInterval(interval);
  };

  const handleCloseModal = () => {
    setOpenUploadWithDispatch(false);
    setFormDataWithDispatch(null);
    setUploadProgressWithDispatch(0);
    setUploadingWithDispatch(false);
    setSuccessfullyUploadedWithDispatch(false);
    setUploadErrorMessageWithDispatch('');
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const downloadRewardReport = useCallback(
    createDownloadRewardReport({dispatch, downloadFromBlobUrl}),
    [dispatch, downloadFromBlobUrl]
  );

  return {
    rewardCatalogData,
    rewardCatalogColConfigData: RewardCatalogListColConfig,
    isLoading,
    isFetching,
    customRules,
    customRulesRewards,
    uploadProgress,
    uploading,
    successfullyUploaded,
    uploadErrorMessage,
    formData,
    setFormData: setFormDataWithDispatch,
    openUpload,
    setOpenUpload: setOpenUploadWithDispatch,
    setUploadProgress: setUploadProgressWithDispatch,
    setUploading: setUploadingWithDispatch,
    setSuccessfullyUploaded: setSuccessfullyUploadedWithDispatch,
    setUploadErrorMessage: setUploadErrorMessageWithDispatch,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    setSearchArrImmediate,
    pageIndex,
    sortingObj,
    searchArr,
    handleResetAll,
    onDownloadBtnClick,
    dropdownList,
    handleDownloadTemplate,
    handleBulkUpload,
    handleCloseNotification,
    handleCloseModal,
    downloadRewardReport
  };
};
